import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { IGroupMember } from 'APP/Tasks/group/participants/getParticipants';
import { MyProfilePopup } from 'APP/main/PopupManager/Popups/MyProfilePopup/MyProfilePopup';
import { UserProfilePopup } from 'APP/main/PopupManager/Popups/UserProfilePopup/UserProfilePopup';
import { Group } from 'APP/store/Groups/Group';
import Entities from 'STORE';

import { AddParticipantsPopup } from '../AddParticipantsPopup/AddParticipantsPopup';
import type {
  IParticipantsPopupParams,
  IParticipantsPopupPresenter,
} from './ParticipantsPopup.types';

export const useParticipantsPopupPresenter = ({
  params,
  onClose,
  onBack,
  popupInstance,
}: ICommonPopupsProps<IParticipantsPopupParams>): IParticipantsPopupPresenter => {
  const { t } = useTranslation();

  const { groupId } = params!;

  const presenter = useLocalStore<IParticipantsPopupPresenter>(() => ({
    searchText: '',
    childPopup: null,

    get group(): Group | null {
      return groupId ? Entities.GroupsStore.getGroupById(groupId) : null;
    },

    get hasBackButton(): boolean {
      return !!onBack;
    },

    get title(): string {
      return presenter.group?.isChannel
        ? t('common_subscribers')
        : t('chat_details_group_members_label');
    },

    onAddParticipantClick(onSuccess: () => void): void {
      if (!presenter.group) {
        return;
      }

      popupInstance.hide();

      presenter.childPopup = Tasks.app.openPopup(AddParticipantsPopup, {
        onClose: (): void => presenter.onClose(),
        onBack: (): void => popupInstance.show(),
        params: {
          groupId: presenter.group.id,
          onSuccess,
        },
      });
    },

    onParticipantClick(participant: IGroupMember): void {
      popupInstance.hide();
      const onBack = (): void => popupInstance.show();

      presenter.childPopup = participant.user.isMe
        ? Tasks.app.openPopup(MyProfilePopup, {
            onBack,
            params: { hasBackButton: true },
          })
        : Tasks.app.openPopup(UserProfilePopup, {
            onClose: (): void => presenter.onClose(),
            onBack,
            params: { userId: participant.user.id, hasBackButton: true },
          });
    },

    onSearch(searchText: string): void {
      presenter.searchText = searchText;
    },

    onClose(): void {
      popupInstance.close();
      onClose?.();
    },

    onBack(): void {
      popupInstance.close();
      onBack?.();
    },
  }));

  useEffect(() => {
    return () => presenter.childPopup?.close();
  }, []);

  return presenter;
};
